/** @jsxImportSource @emotion/react */
import { useTheme } from '@mui/material/styles'
import { legendMenu, mapLegends } from './MapLegends.styles'
import { Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { Legend, LegendSection } from '@app/components/Molecules/Legend'
import { Add, Remove } from '@mui/icons-material'
import { useCallback, useEffect, useMemo, useState } from 'react'

export function MapLegends({
  legendIds,
  uiBlueLegendTitles = [],
  showVulnerabilityLegend = false,
}: {
  legendIds: string[]
  uiBlueLegendTitles?: string[]
  showVulnerabilityLegend?: boolean
}) {
  const theme = useTheme()

  const { legendsData: fullLegendsData } = useSelector((state: RootState) => state.riskMap)

  const [legendsMinimized, setLegendsMinimized] = useState<{ [key: string]: boolean }>({})

  const allMinimized = useMemo(
    () => Object.values(legendsMinimized).every((value) => value),
    [legendsMinimized],
  )

  const legendsData = useMemo(() => {
    // filter out legends for layers that aren't visible
    const filteredLegendsData: { [key: string]: { title: string; sections: LegendSection[] } } = {}
    Object.entries(fullLegendsData).forEach(([key, value]) => {
      if (legendIds.includes(key)) {
        filteredLegendsData[key] = value
      }
    })
    return filteredLegendsData
  }, [fullLegendsData, legendIds])

  const toggleAllLegends = useCallback(() => {
    setLegendsMinimized((oldState) => {
      const newState: { [key: string]: boolean } = {}
      Object.keys(oldState).forEach((key) => {
        newState[key] = !allMinimized
      })
      return newState
    })
  }, [allMinimized])

  // Close legends when entering fullscreen
  useEffect(() => {
    function onEnterFullscreen() {
      setLegendsMinimized((oldState) => {
        const newState: { [key: string]: boolean } = {}
        Object.keys(oldState).forEach((key) => {
          newState[key] = true
        })
        return newState
      })
    }

    document.addEventListener('EnteringFullScreen', onEnterFullscreen)

    return () => document.removeEventListener('EnteringFullScreen', onEnterFullscreen)
  }, [])
  useEffect(() => {
    const newState: { [key: string]: boolean } = {}
    Object.entries(legendsMinimized).forEach(([key, minimized]) => {
      if (legendIds.includes(key) || key === 'ui-blue-legend') {
        newState[key] = minimized
      }
    })
    Object.keys(legendsData).forEach((key) => {
      if (legendIds.includes(key)) {
        if (!(key in newState)) newState[key] = false
      }
    })
    // Add ui-blue-legend if needed
    if (uiBlueLegendTitles.length > 0 && newState['ui-blue-legend'] === undefined) {
      newState['ui-blue-legend'] = false
    }
    // Compare the previousState with the newState to avoid the loop, this is a workaround
    const prevState = JSON.stringify(legendsMinimized)
    const nextState = JSON.stringify(newState)
    if (prevState !== nextState) {
      setLegendsMinimized(newState)
    }
  }, [legendsData, legendIds, uiBlueLegendTitles, legendsMinimized])

  const uiBlueLegends = useMemo(() => {
    return uiBlueLegendTitles.map((title) => ({
      label: title,
      color: '#0a2845',
    }))
  }, [uiBlueLegendTitles])

  return (
    <Box css={mapLegends({ theme })}>
      {Object.keys(legendsData).length > 0 && (
        <Box css={legendMenu({ theme })} onClick={toggleAllLegends}>
          {allMinimized ? (
            <>
              Expand legends
              <Add />
            </>
          ) : (
            <>
              Collapse legends
              <Remove />
            </>
          )}
        </Box>
      )}
      {showVulnerabilityLegend && legendsData['vulnerability'] && (
        <Legend
          key={'vulnerability'}
          title={legendsData['vulnerability'].title}
          sections={legendsData['vulnerability'].sections}
          minimized={legendsMinimized['vulnerability']}
          toggleMinimized={() => {
            setLegendsMinimized((oldState) => ({
              ...oldState,
              ['vulnerability']: !oldState['vulnerability'],
            }))
          }}
        />
      )}
      {Object.entries(legendsData).map(([key, value]) => {
        if (legendsMinimized[key] === undefined || key == 'vulnerability') return null
        return (
          <Legend
            key={key}
            title={value.title}
            sections={value.sections}
            minimized={legendsMinimized[key]}
            toggleMinimized={() => {
              setLegendsMinimized((oldState) => ({
                ...oldState,
                [key]: !oldState[key],
              }))
            }}
          />
        )
      })}
      {uiBlueLegends && uiBlueLegends.length > 0 && (
        <Legend
          key={'ui-blue-legend'}
          title="Elements"
          minimized={legendsMinimized['ui-blue-legend']}
          toggleMinimized={() => {
            setLegendsMinimized((oldState) => ({
              ...oldState,
              ['ui-blue-legend']: !oldState['ui-blue-legend'],
            }))
          }}
          sections={[{ type: 'categorical', items: uiBlueLegends }]}
        />
      )}
    </Box>
  )
}
